import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { WidgetProps } from '@/lib/widgets/types';
import { Wrench, Calendar, AlertTriangle, Plus } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function MaintenanceCalendarWidget({ isEditing = false }: WidgetProps) {
  const maintenances = [
    {
      id: 1,
      vehicle: 'ABC-1234',
      type: 'Troca de óleo',
      scheduledDate: new Date(),
      status: 'scheduled',
      priority: 'medium',
      km: 15000
    },
    {
      id: 2,
      vehicle: 'DEF-5678',
      type: 'Revisão geral',
      scheduledDate: addDays(new Date(), 2),
      status: 'overdue',
      priority: 'high',
      km: 25000
    },
    {
      id: 3,
      vehicle: 'GHI-9012',
      type: 'Alinhamento',
      scheduledDate: addDays(new Date(), 5),
      status: 'scheduled',
      priority: 'low',
      km: 18000
    },
    {
      id: 4,
      vehicle: 'JKL-3456',
      type: 'Troca de pneus',
      scheduledDate: addDays(new Date(), 7),
      status: 'scheduled',
      priority: 'medium',
      km: 22000
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityIcon = (priority: string) => {
    if (priority === 'high') return <AlertTriangle className="h-3 w-3 text-red-500" />;
    return null;
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Calendário de Manutenção
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={isEditing}>
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {maintenances.slice(0, 4).map((maintenance) => (
            <div key={maintenance.id} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{maintenance.vehicle}</p>
                    {getPriorityIcon(maintenance.priority)}
                  </div>
                  <p className="text-xs text-muted-foreground">{maintenance.type}</p>
                </div>
                <Badge className={`text-xs ${getStatusColor(maintenance.status)}`}>
                  {maintenance.status === 'scheduled' ? 'Agendado' : 
                   maintenance.status === 'overdue' ? 'Atrasado' : 'Concluído'}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(maintenance.scheduledDate, "dd/MM", { locale: ptBR })}
                </div>
                <span>{maintenance.km.toLocaleString()} km</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}